import {testIdProps} from '@github-ui/test-id-props'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, type IconButtonProps} from '@primer/react'
import {merge} from '@primer/styled-react'
import {Fragment, useMemo} from 'react'

import {useActionBarContent} from './ActionBarContentContext'
import {useActionBarRef} from './ActionBarRefContext'
import {useActionBarResize} from './ActionBarResizeContext'
import styles from './OverflowMenu.module.css'

export type OverflowMenuProps = {
  anchorProps?: Omit<IconButtonProps, 'aria-labelledby'>
  /** Optional controlled open state for the overflow menu. */
  open?: boolean
  /** Callback when the menu requests an open state change. */
  onOpenChange?: (open: boolean) => void
}

const defaultMenuToggleIcon = KebabHorizontalIcon
const defaultMenuToggleVariant = 'invisible'

export const OverflowMenu = ({
  anchorProps: {
    'aria-label': ariaLabel,
    icon = defaultMenuToggleIcon,
    variant = defaultMenuToggleVariant,
    ...otherAnchorProps
    // eslint-disable-next-line @eslint-react/no-unstable-default-props
  } = {
    icon: defaultMenuToggleIcon,
    variant: defaultMenuToggleVariant,
  },
  open,
  onOpenChange,
}: OverflowMenuProps) => {
  const {anchorRef} = useActionBarRef()
  const {actions, staticMenuActions, label} = useActionBarContent()
  const {visibleChildEndIndex} = useActionBarResize()

  const anchorProps = useMemo(
    () =>
      merge(otherAnchorProps, {
        'aria-label': ariaLabel?.trim() || `More ${label}`,
        icon,
        className: styles.IconButton_0,
        variant,
      }),
    [ariaLabel, icon, label, variant, otherAnchorProps],
  )

  const overflowActions = useMemo(() => actions?.slice(visibleChildEndIndex), [actions, visibleChildEndIndex])

  const hasOverflowActions = overflowActions && overflowActions.length > 0
  const hasStaticMenuActions = staticMenuActions && staticMenuActions.length > 0

  if (!hasOverflowActions && !hasStaticMenuActions) return null

  return (
    <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={onOpenChange}>
      <ActionMenu.Anchor>
        <IconButton {...testIdProps('overflow-menu-anchor')} {...anchorProps} />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end" data-overflow-menu-overlay>
        <ActionList>
          {hasStaticMenuActions &&
            staticMenuActions.map(staticMenuAction => (
              <Fragment key={staticMenuAction.key}>{staticMenuAction.render()}</Fragment>
            ))}

          {overflowActions?.map(action => <Fragment key={action.key}>{action.render(true)}</Fragment>)}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ OverflowMenu.displayName ||= 'OverflowMenu' } catch {}